import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useState } from "react";
import type { AdminUser } from "../types";
import { useApiMutation } from "@/hooks/useApiMutation";

export function useAdminUsers(enabled: boolean = false) {
  const [roleChangeConfirm, setRoleChangeConfirm] = useState<{ userId: string; email: string; newRole: string } | null>(null);
  const [accessToggleConfirm, setAccessToggleConfirm] = useState<{ userId: string; email: string; currentStatus: boolean } | null>(null);

  const { data: usersData, isLoading: usersLoading } = useQuery<AdminUser[] | { data: AdminUser[] }>({
    queryKey: ["/api/admin/users"],
    enabled,
  });

  const users = Array.isArray(usersData) ? usersData : (usersData?.data || []);

  const updateRoleMutation = useApiMutation({
    endpoint: (vars: { userId: string; role?: string }) => `/api/admin/users/${vars.userId}/role`,
    method: "PATCH",
    invalidate: [["/api/admin/users"]],
    successMessage: "User role updated successfully",
    onSuccess: () => {
      setRoleChangeConfirm(null);
    },
    onError: () => {
      setRoleChangeConfirm(null);
    },
  });

  const toggleAccessMutation = useApiMutation({
    endpoint: (vars: { userId: string; isActive?: boolean }) => `/api/admin/users/${vars.userId}/access`,
    method: "PATCH",
    invalidate: [["/api/admin/users"]],
    successMessage: "User access updated successfully",
    onSuccess: () => {
      setAccessToggleConfirm(null);
    },
    onError: () => {
      setAccessToggleConfirm(null);
    },
  });

  const handleRoleChange = (userId: string, email: string, newRole: string) => {
    setRoleChangeConfirm({ userId, email, newRole });
  };

  const confirmRoleChange = () => {
    if (roleChangeConfirm) {
      updateRoleMutation.mutate({ userId: roleChangeConfirm.userId, role: roleChangeConfirm.newRole });
    }
  };

  const handleAccessToggle = (userId: string, email: string, currentStatus: boolean) => {
    setAccessToggleConfirm({ userId, email, currentStatus });
  };

  const confirmAccessToggle = () => {
    if (accessToggleConfirm) {
      toggleAccessMutation.mutate({ userId: accessToggleConfirm.userId, isActive: !accessToggleConfirm.currentStatus });
    }
  };

  return {
    users,
    usersLoading,
    roleChangeConfirm,
    accessToggleConfirm,
    handleRoleChange,
    confirmRoleChange,
    handleAccessToggle,
    confirmAccessToggle,
    setRoleChangeConfirm,
    setAccessToggleConfirm,
    isUpdating: updateRoleMutation.isPending || toggleAccessMutation.isPending,
  };
}
